/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import deferToClientSideRender from 'js/lib/deferToClientSideRender';
import { useRetracked } from 'js/lib/retracked';

import { Grid, PageGridContainer, Typography2, breakpoints } from '@coursera/cds-core';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import PromoNotificationBar from 'bundles/coursera-plus-common/components/elevateLTV/PromoNotificationBar';
import CourseraPlusNotificationBanner from 'bundles/coursera-plus-common/components/lihp/CourseraPlusNotificationBanner';
import { previewShouldShowPostPurchaseChanges } from 'bundles/coursera-plus-common/utils/subscriptionsForwardDiscoveryExperiment';
import { useNotificationBannersHandler } from 'bundles/coursera-plus-common/utils/useNotificationBannersHandler';

import _t from 'i18n!nls/coursera-plus';

const styles = {
  pageContainer: css`
    margin-top: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down('sm')} {
      margin: var(--cds-spacing-200) 0;
    }
  `,
  bannerContainer: css`
    background-color: var(--cds-color-interactive-background-primary-hover-weak);
    border-radius: 4px;
    padding: 0 !important;
    margin: var(--cds-spacing-200);
  `,
  content: css`
    text-align: center;

    ${breakpoints.down('xs')} {
      text-align: left;
    }

    padding: var(--cds-spacing-200) 0;
  `,
};

const CourseraPlusWelcomeNotification = () => {
  /*
  Show the Coursera Plus welcome notification banner only if:
  - The learner has an active Coursera Plus subscription.
  - The 'isCourseraPlusSuccess' parameter is true in the URL.
  - The learner is not eligible for reactivation of a non-Coursera Plus subscription.
*/
  const { cplusWelcomeNotificationBannerData } = useNotificationBannersHandler();
  const storageKey = cplusWelcomeNotificationBannerData?.storageKey ?? '';
  const [isDismissed, setIsDismissed] = React.useState<boolean>(() =>
    localStorageEx.getItem(storageKey, JSON.parse, false)
  );
  const trackEvent = useRetracked();

  if (!cplusWelcomeNotificationBannerData.showBanner) {
    return null;
  }

  const content = (
    <>
      <Typography2 component="h2" variant="subtitleMedium">
        {_t('Congrats on your new Coursera Plus subscription!')}
      </Typography2>
      <Typography2 component="p" variant="bodySecondary">
        {_t('Explore all the content included in your subscription to reach your learning goal.')}
      </Typography2>
    </>
  );

  if (!isDismissed && previewShouldShowPostPurchaseChanges()) {
    return (
      <PageGridContainer maxWidth="lg" css={styles.pageContainer}>
        <Grid container item lg={12} css={styles.bannerContainer} spacing={0} alignItems="center">
          <PromoNotificationBar
            title={_t('Welcome to Coursera Plus')}
            subtitle={_t("You've unlocked thousands of courses, guidance from Coursera Coach, and more.")}
            displayBgImage={true}
            dismissable={true}
            isDismissed={isDismissed}
            onDismiss={() => {
              localStorageEx.setItem(cplusWelcomeNotificationBannerData.storageKey, true, JSON.stringify);
              setIsDismissed(true);
            }}
            ctaClickTrackingCallback={() => {
              trackEvent({
                action: 'click',
                trackingName: 'dismiss_cplus_welcome_notification_banner',
                trackingData: {},
              });
            }}
          />
        </Grid>
      </PageGridContainer>
    );
  }

  return (
    <CourseraPlusNotificationBanner content={content} storageKey={cplusWelcomeNotificationBannerData.storageKey} />
  );
};

export default deferToClientSideRender(CourseraPlusWelcomeNotification);
